<template>
    <div class="lg:mt-8 lg:px-4 lg:max-w-6xl lg:mx-auto">
        <div class="bg-white rounded-lg shadow overflow-hidden" :key="key">
            <div class="divide-y divide-gray-200 lg:grid lg:grid-cols-12 lg:divide-y-0 lg:divide-x">
                <aside v-if="!isMobile() || (isMobile() && active_thread === null)"
                       class="py-6 lg:col-span-3 overflow-auto lg:h-screen-with-index">
                    <ul class="divide-y divide-gray-200">
                        <li v-for="thread in threads"
                            :class="{'bg-gray-200 font-bold': thread.id === active_thread, 'py-4 cursor-pointer hover:bg-gray-100': true}"
                            :key="key + '-thread-' + thread.id"
                            @click.prevent="handleActiveThreadChange(thread.id)">
                            <div class="flex space-x-3 px-3">
                                <img class="h-6 w-6 rounded-full object-cover"
                                     :src="getOtherUserFromThread(thread).avatar_url"
                                     alt="avatar">

                                <div class="flex-1 space-y-1"
                                     :class="{'font-bold': hasUnseenMessages(thread)}">
                                    <div class="flex items-center justify-between">
                                        <h3 class="text-sm font-medium">
                                            {{ getOtherUserFromThread(thread).name }}
                                        </h3>
                                        <p class="text-sm text-gray-500">
                                            {{ getLastMessageTime(thread) }}
                                        </p>
                                    </div>
                                    <p class="text-sm text-gray-500 thread-excerpt">
                                        {{ getThreadExcerpt(thread) }}
                                    </p>
                                </div>
                            </div>
                        </li>
                    </ul>
                </aside>

                <div v-for="thread in threads"
                     v-show="active_thread === thread.id"
                     :key="'thread-'+thread.id"
                     :id="'thread-'+thread.id"
                     class="thread-content divide-y divide-gray-200 lg:col-span-9 relative">

                    <div v-if="isMobile()"
                         class="flex items-center p-2 shadow-sm fixed z-50 top-[65px] left-0 bg-white w-full">
                        <button type="button" @click.prevent="handleActiveThreadChange(null)">
                            <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 8.13 13.25">
                                <polyline style="fill:none;stroke:#58595b;stroke-miterlimit:10;stroke-width:2px"
                                          points="7.42 12.54 1.42 6.71 7.42 0.71"/>
                            </svg>
                        </button>
                        <span class="px-2">{{ getOtherUserFromThread(thread).name }}</span>
                        <img class="h-6 w-6 rounded-full object-cover"
                             :src="getOtherUserFromThread(thread).avatar_url"
                             alt="">
                    </div>

                    <div class="p-4" v-if="thread.messages.length === 0">
                        Envoyez un premier message à <strong>{{ getOtherUserFromThread(thread).name }}</strong> afin de lancer la discussion
                    </div>

                    <div class="sticky top-0 z-40 mx-4 mt-6 mb-6">
                        Attention, pour votre sécurité, ne partagez jamais d'informations personnelles et ne répondez
                        jamais aux sollicitations d'argent que vous pourriez recevoir. En cas de doute, n'hésitez pas à
                        remonter un abus via le bouton signalé
                    </div>

                    <ul :id="'thread-'+thread.id+'-container'"
                        class="pt-4 mb-16 overflow-auto relative h-screen-without-message"
                        ref="messageContainer">
                        <li v-for="message in thread.messages"
                            :key="message.id"
                            :class="{'flex-row-reverse': isCurrentUserMessage(message), 'flex-row': !isCurrentUserMessage(message), 'flex justify-start items-center mx-4': true}">
                            <img class="h-6 w-6 rounded-full object-cover"
                                 :src="message.user && message.user.avatar_url"
                                 :alt="message.user && message.user.name"
                                 :title="message.user && message.user.name">
                            <div :class="{'bg-pink-600': isCurrentUserMessage(message), 'bg-gray-400': !isCurrentUserMessage(message), 'text-base text-white w-3/4 mx-4 my-1 p-2 rounded-lg': true}">
                                <span v-html="formatMessage(message.content)"></span>
                            </div>
                        </li>
                    </ul>

                    <div class="flex justify-between bg-cap-pink-light bottom-0 w-full">
                        <label for="content" class="hide">Message</label>
                        <textarea
                            id="content"
                            class="flex-grow m-2 py-2 px-4 mr-1 border border-gray-300 bg-white resize-none thread-textarea"
                            rows="1"
                            ref="messageBox"
                            name="content"
                            placeholder="Message..."
                            style="outline: none;"
                            v-model="message"
                            :disabled="is_sending"
                            @keyup.enter.exact.prevent="handleSendMessage"
                        ></textarea>
                        <button class="m-2"
                                style="outline: none;"
                                @click="handleSendMessage"
                                :disabled="!canSendMessage">
                            <span v-if="is_sending" class="p-2 flex flex-row justify-end status-bar">
                                <span class="loader -sm"></span>
                            </span>
                            <svg v-else
                                 class="svg-inline--fa text-pink-500 text-cap-pink-normal fa-paper-plane fa-w-16 w-12 h-12 py-2 mr-2"
                                 focusable="false"
                                 data-prefix="fas"
                                 data-icon="paper-plane"
                                 role="img"
                                 xmlns="http://www.w3.org/2000/svg"
                                 viewBox="0 0 512 512">
                                <path
                                    fill="currentColor"
                                    d="M476 3.2L12.5 270.6c-18.1 10.4-15.8 35.6 2.2 43.2L121 358.4l287.3-253.2c5.5-4.9 13.3 2.6 8.6 8.3L176 407v80.5c0 23.6 28.5 32.9 42.5 15.8L282 426l124.6 52.2c14.2 6 30.4-2.9 33-18.2l72-432C515 7.8 493.3-6.8 476 3.2z"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Axios from "axios";
import { debounce } from 'lodash-es';

export default {
    name: 'ChatComponent',
    props: {
        current_user: {
            type: Object,
            required: true
        },
        base_url: {
            type: String,
            required: true
        },
    },

    data() {
        return {
            key: 0,
            polling: null,
            threads: [],
            is_loading: false,
            is_sending: false,
            active_thread: null,
            message: null,
        }
    },

    computed: {
        canSendMessage() {
            return this.message != null && this.message.trim() !== '' && !this.is_sending;
        },
    },

    watch: {
        active_thread() {
            this.$nextTick(() => this.scrollToBottom());
        }
    },

    created() {
        this.debouncedPollData = debounce(this.pollData, 500);
    },

    beforeMount() {
        this.pollData();
        this.polling = setInterval(() => {
            this.debouncedPollData();
        }, 5000);
    },

    mounted() {
        const anchor = window.location.hash.slice(1);
        if (anchor && !this.isMobile()) {
            const thread_id = anchor.split('-')[1];
            this.handleActiveThreadChange(thread_id);
        }
    },

    beforeDestroy() {
        clearInterval(this.polling);
    },

    methods: {
        getOtherUserFromThread(thread) {
            const otherUser = thread.users.find(user => user.id !== this.current_user.id);
            return otherUser || {};
        },

        hasUnseenMessages(thread) {
            return thread.messages.some(message => !message.is_seen);
        },

        isCurrentUserMessage(message) {
            return message.user && message.user.id === this.current_user.id;
        },

        formatMessage(content) {
            if (!content) return '';
            return content.replace(/\n/g, '<br />');
        },

        getLastMessageTime(thread) {
            const lastMessage = thread.messages
                .filter(message => message.user && message.user.id !== this.current_user.id)
                .slice(-1)[0];
            return lastMessage ? this.parseDiffForHumans(lastMessage.created_at) : '';
        },

        getThreadExcerpt(thread) {
            if (!thread.messages.length) {
                return "Vous n'avez pas encore écrit";
            }
            const lastMessage = thread.messages
                .filter(message => message.user && message.user.id !== this.current_user.id)
                .slice(-1)[0];
            return lastMessage ? lastMessage.content : "N'a pas encore répondu";
        },

        async pollData() {
            if (this.is_loading) return;

            this.is_loading = true;
            try {
                const response = await Axios.get(`${this.base_url}/threads/all`);
                this.threads = response.data;

                if (this.active_thread === null && !this.isMobile()) {
                    this.active_thread = this.threads[0]?.id;
                    if (this.active_thread) {
                        await Axios.get(`${this.base_url}/threads/${this.active_thread}/seen`);
                    }
                }

                this.$nextTick(() => this.scrollToBottom());
            } catch (error) {
                console.error('Erreur lors du chargement des messages:', error);
            } finally {
                this.is_loading = false;
            }
        },

        parseDiffForHumans(since) {
            const now = new Date();
            const date = new Date(since);
            const diff = now - date;

            const intervals = {
                'a': 31536000000, // année
                'j': 86400000,    // jour
                'h': 3600000,     // heure
                'm': 60000,       // minute
                's': 1000         // seconde
            };

            for (const [unit, ms] of Object.entries(intervals)) {
                if (diff >= ms) {
                    return Math.round(diff / ms) + unit;
                }
            }

            return '...';
        },

        scrollToBottom() {
            if (this.active_thread) {
                const container = document.querySelector(`#thread-${this.active_thread}-container`);
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
            }
        },

        async handleSendMessage(event) {
            event?.preventDefault();

            if (!this.canSendMessage) return;

            const messageContent = this.message.trim();
            this.is_sending = true;
            this.message = null;

            try {
                const response = await Axios.post(
                    `${this.base_url}/threads/${this.active_thread}/send`,
                    { content: messageContent }
                );

                const threadIndex = this.threads.findIndex(thread => response.data.id === thread.id);
                if (threadIndex !== -1) {
                    this.$set(this.threads, threadIndex, response.data);
                }

                await this.pollData();
                this.$nextTick(() => {
                    this.$refs.messageBox[0].focus();
                });
            } catch (error) {
                console.error('Erreur lors de l\'envoi du message:', error);
            } finally {
                this.is_sending = false;
            }
        },

        async handleActiveThreadChange(thread_id) {
            this.active_thread = thread_id ? Number(thread_id) : null;
            if (thread_id) {
                await Axios.get(`${this.base_url}/threads/${thread_id}/seen`);
            }
        },

        isMobile() {
            return window.innerWidth < 1024;
        }
    }
}
</script>
